import React, { memo, useEffect, useLayoutEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { getSummaryInfo } from 'actions/applicants'
import strings from 'strings'
import './summaryStrip.scss'

const summaryStrings = strings.APPLICANT.SUMMARY

const ROW_HEIGHT = 30

const toChips = (section = []) => section
    .filter(item => item && Array.isArray(item.childs))
    .flatMap((item, itemIndex) => (
        item.childs.length === 1
            ? [{
                key: `${itemIndex}-0`,
                label: item.name,
                description: item.childs[0].description,
                status: item.childs[0].status
            }]
            : item.childs.map((child, childIndex) => ({
                key: `${itemIndex}-${childIndex}`,
                label: `${item.name} · ${child.entityName}`,
                description: child.description,
                status: child.status
            }))
    ))

const SummarySection = ({ title, chips }) => {
    const [open, setOpen] = useState(false)
    const [hasMore, setHasMore] = useState(false)
    const contentRef = useRef(null)

    useLayoutEffect(() => {
        const content = contentRef.current
        if (!content) return undefined

        const measure = () => setHasMore(content.scrollHeight > ROW_HEIGHT + 1)
        measure()

        window.addEventListener('resize', measure)
        return () => window.removeEventListener('resize', measure)
    }, [chips])

    return (
        <div className={`summaryStrip__section${open ? ' summaryStrip__section--open' : ''}`}>
            <span className='summaryStrip__sectionTitle'>{title}</span>

            <div className='summaryStrip__viewport'>
                <div className='summaryStrip__items' ref={contentRef}>
                    {chips.map(chip => (
                        <div className='summaryStrip__item' key={chip.key}>
                            <span className='summaryStrip__label'>{chip.label}</span>
                            <span className='summaryStrip__value'>{chip.description}</span>
                            <span className={`itemStatus ${String(chip.status).toLowerCase()}StatusColor`} />
                        </div>
                    ))}
                </div>
            </div>

            {hasMore &&
                <button
                    type='button'
                    className='summaryStrip__toggle'
                    onClick={() => setOpen(!open)}
                    aria-expanded={open}
                    aria-label={`${open ? 'Collapse' : 'Expand'} ${title}`}
                >
                    <span className='summaryStrip__chevron' />
                </button>
            }
        </div>
    )
}

SummarySection.propTypes = {
    title: PropTypes.string.isRequired,
    chips: PropTypes.array.isRequired
}

const SummaryStrip = memo(function SummaryStrip({ applicantId, updates }) {
    const [sections, setSections] = useState({ summary: [], insights: [] })
    const updateFromRedux = useSelector(state => state.applicant?.updateSummary || 0)

    useEffect(() => {
        if (!applicantId) return undefined

        let cancelled = false
        getSummaryInfo(applicantId)()
            .then((res) => {
                if (cancelled) return
                setSections({
                    summary: toChips(res?.summary),
                    insights: toChips(res?.additionalSummary)
                })
            })
            .catch((error) => {
                console.error('Error fetching summary info:', error)
                if (!cancelled) setSections({ summary: [], insights: [] })
            })

        return () => { cancelled = true }
    }, [applicantId, updates, updateFromRedux])

    const groups = [
        { key: 'summary', title: summaryStrings.SUMMARY_TITLE, chips: sections.summary },
        { key: 'insights', title: summaryStrings.INSIGHTS_TITLE, chips: sections.insights }
    ].filter(group => group.chips.length)

    if (!groups.length) return null

    return (
        <div className='summaryStrip'>
            {groups.map(group => (
                <SummarySection key={group.key} title={group.title} chips={group.chips} />
            ))}
        </div>
    )
})

SummaryStrip.propTypes = {
    applicantId: PropTypes.string,
    updates: PropTypes.number
}

export default SummaryStrip
